import React from "react";
import { FormHelperText } from "@mui/material";
import { useFeatureFlag } from "../../hooks/useFeatureFlag";

// Patriot alerts deliver either an MP4 clip (patriot_send_video on; ignore
// zones drawn onto it when draw_ignore_zones is on) or a link to the alert
// page. Once the connector adds `&iz=1` to that link (the alert page then
// draws the zones), draw_ignore_zones applies with send video off too. That
// option is behind the LaunchDarkly flag below (default off) so the UI can
// ship before the connector change is live. See PatriotDeliveryChoice.
export const PATRIOT_IGNORE_ZONES_ON_ALERT_PAGE_FLAG =
  "patriotIgnoreZonesOnAlertPage";

/** True when the alert-page link also draws ignore zones (flag on). */
export const usePatriotIgnoreZonesOnAlertPage = (): boolean => {
  const flags = useFeatureFlag()?.flags;
  return flags?.[PATRIOT_IGNORE_ZONES_ON_ALERT_PAGE_FLAG] === true;
};

export const isPatriotSendVideoOn = (value: unknown): boolean =>
  value === true || value === "True" || value === "true";

export const PATRIOT_DELIVERY_SUMMARY = {
  link: "Patriot receives: a link to the Actuate alert page (clip plays there; no ignore zones).",
  // Alert-page flag on: the link can carry ignore zones, so drop "no ignore zones".
  linkAlertPage:
    "Patriot receives: a link to the Actuate alert page (clip plays there).",
  linkWithZones:
    "Patriot receives: a link to the Actuate alert page with ignore zones drawn.",
  video: "Patriot receives: an MP4 video clip of the alert.",
  videoWithZones:
    "Patriot receives: an MP4 video clip of the alert with ignore zones drawn.",
} as const;

/** Plain-language line describing what a Patriot alert actually delivers. */
export const patriotDeliverySummary = ({
  sendVideo,
  drawIgnoreZones,
  ignoreZonesOnAlertPage = false,
}: {
  sendVideo: unknown;
  drawIgnoreZones: unknown;
  ignoreZonesOnAlertPage?: boolean;
}): string => {
  if (!isPatriotSendVideoOn(sendVideo)) {
    if (!ignoreZonesOnAlertPage) {
      return PATRIOT_DELIVERY_SUMMARY.link;
    }
    return isPatriotSendVideoOn(drawIgnoreZones)
      ? PATRIOT_DELIVERY_SUMMARY.linkWithZones
      : PATRIOT_DELIVERY_SUMMARY.linkAlertPage;
  }
  return isPatriotSendVideoOn(drawIgnoreZones)
    ? PATRIOT_DELIVERY_SUMMARY.videoWithZones
    : PATRIOT_DELIVERY_SUMMARY.video;
};

/** Small grey helper line rendering {@link patriotDeliverySummary}. */
export const PatriotDeliverySummary = ({
  sendVideo,
  drawIgnoreZones,
}: {
  sendVideo: unknown;
  drawIgnoreZones: unknown;
}) => {
  const ignoreZonesOnAlertPage = usePatriotIgnoreZonesOnAlertPage();
  return (
    <FormHelperText
      data-testid="patriot-delivery-summary"
      sx={{ fontFamily: "Mulish", marginLeft: "1vw" }}
    >
      {patriotDeliverySummary({
        sendVideo,
        drawIgnoreZones,
        ignoreZonesOnAlertPage,
      })}
    </FormHelperText>
  );
};
